import type { Locale } from '../translations';
import { DEFAULT_SITE_NAME } from '../constants/brand';

export const DEFAULT_HERO_BG = '/assets/img/ridersoficarus.jpg';

/** Palavras-chave principais (EN + PT misturado para buscas BR e globais). */
export const SEO_KEYWORDS_BY_LOCALE: Record<Locale, string> = {
    en: [
        'Empire Icarus',
        'Empire Icarus Online',
        'Icarus Online',
        'Riders of Icarus',
        'Icarus KR',
        'Icarus MMORPG',
        'dragon mount MMORPG',
        'tame dragons',
        'flying mount game',
        'familiar taming',
        'Hakanas',
        'free MMORPG',
        'mounted combat',
        'Icarus private server',
        'Icarus download',
    ].join(', '),
    'pt-BR': [
        'Empire Icarus',
        'Empire Icarus Online',
        'Icarus Online',
        'Riders of Icarus',
        'servidor privado Icarus',
        'servidor privado Riders of Icarus',
        'servidor Icarus Brasil',
        'Icarus BR',
        'Icarus KR',
        'MMORPG Icarus',
        'download Riders of Icarus',
        'como baixar Icarus',
        'cadastro Icarus',
        'montaria dragão',
        'domar dragões',
        'Hakanas',
    ].join(', '),
};

export function getSeoKeywords(locale: Locale): string {
    return SEO_KEYWORDS_BY_LOCALE[locale === 'pt-BR' ? 'pt-BR' : 'en'];
}

export type HomeSeoDefaults = {
    metaTitle: string;
    metaDescription: string;
    h1Line1: string;
    h1Highlight: string;
    heroSubtitle: string;
    heroDescription: string;
    h2Download: string;
    h2Features: string;
};

export function getHomeSeoDefaults(locale: Locale, _siteName: string): HomeSeoDefaults {
    if (locale === 'pt-BR') {
        return {
            metaTitle:
                'Empire Icarus Online - Servidor Privado de Riders of Icarus Brasil',
            metaDescription:
                'Venha jogar no Empire Icarus Online, o melhor servidor privado de Riders of Icarus. Servidor BR/EU estável, eventos diários, downloads e cadastro rápido!',
            h1Line1: 'Servidor de Icarus',
            h1Highlight: 'Online',
            heroSubtitle: 'Empire Icarus Online · Riders of Icarus',
            heroDescription:
                'Jogue no melhor servidor privado de Icarus Online e Riders of Icarus no Brasil e Europa: montarias, dragões, eventos diários, rankings e comunidade ativa no Discord.',
            h2Download: 'Como baixar Riders of Icarus',
            h2Features: 'Por que jogar Icarus Online no Empire',
        };
    }

    return {
        metaTitle:
            'Empire Icarus Online - Private Riders of Icarus Server | Icarus Online',
        metaDescription:
            'Play Empire Icarus Online — the best private Riders of Icarus / Icarus Online server. Stable BR/EU realms, daily events, fast download and registration!',
        h1Line1: 'Icarus Online',
        h1Highlight: 'Private Server',
        heroSubtitle: 'Empire Icarus Online · Riders of Icarus',
        heroDescription:
            'Join Empire Icarus Online, a stable private server for Riders of Icarus and Icarus Online: dragon mounts, daily events, rankings, and an active Discord community.',
        h2Download: 'How to Download Riders of Icarus',
        h2Features: 'Why Play Icarus Online on Empire',
    };
}

export function buildNewsArticleJsonLd(options: {
    siteName: string;
    siteUrl: string;
    headline: string;
    description: string;
    path: string;
    datePublished?: string;
    dateModified?: string;
    author?: string;
    imageUrl?: string;
}) {
    const base = options.siteUrl.replace(/\/$/, '');
    const pageUrl = `${base}${options.path.startsWith('/') ? options.path : `/${options.path}`}`;
    const image = options.imageUrl?.startsWith('http')
        ? options.imageUrl
        : options.imageUrl
          ? `${base}${options.imageUrl}`
          : `${base}/assets/img/ridersoficarus.jpg`;

    return {
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        headline: options.headline,
        description: options.description,
        url: pageUrl,
        mainEntityOfPage: { '@type': 'WebPage', '@id': pageUrl },
        datePublished: options.datePublished,
        dateModified: options.dateModified || options.datePublished,
        author: options.author
            ? { '@type': 'Person', name: options.author }
            : { '@type': 'Organization', name: options.siteName },
        publisher: {
            '@type': 'Organization',
            name: options.siteName,
            url: options.siteUrl,
            logo: {
                '@type': 'ImageObject',
                url: `${base}/logo512.png`,
            },
        },
        image: [image],
        inLanguage: ['pt-BR', 'en'],
        isAccessibleForFree: true,
    };
}

export function buildBreadcrumbJsonLd(
    siteUrl: string,
    items: { name: string; path: string }[]
) {
    const base = siteUrl.replace(/\/$/, '');
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: item.path === '/' ? `${base}/` : `${base}${item.path}`,
        })),
    };
}

export function buildHomeJsonLd(siteName: string, siteUrl: string, description: string) {
    return [
        {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: siteName,
            url: siteUrl,
            description,
            inLanguage: ['en', 'pt-BR'],
            potentialAction: {
                '@type': 'SearchAction',
                target: `${siteUrl}/news`,
                'query-input': 'required name=search_term_string',
            },
        },
        {
            '@context': 'https://schema.org',
            '@type': 'VideoGame',
            name: siteName,
            description,
            url: siteUrl,
            genre: ['MMORPG', 'Fantasy'],
            gamePlatform: 'PC',
            applicationCategory: 'Game',
            offers: {
                '@type': 'Offer',
                price: '0',
                priceCurrency: 'USD',
            },
        },
    ];
}
